/**
 * Группы шаблона в «Запусках FB» (спека «Конструктор FB v4» §3.5, §3.6, §11):
 * чистые правила экрана — номера «①», аудиторная группа и её Advantage+,
 * подписи замечаний и названия латиницей. Правила совпадают с auto-ads
 * (`registry/adset-groups.ts`); деньги здесь не считаются (только сервер).
 */
import { ADS_GROUP_LABEL_RE, ADS_GROUP_LIMITS, isAdsGroupLabelValid, } from '@wit-support/admin-contracts';
export const ADVANTAGE_AUDIENCE_KEY = 'adset.advantageAudience';
export const GROUP_BID_KEY = 'adset.bidAmountMinor';
/** Поля, которые делают группу «аудиторной» (§3.6): своё непустое значение. */
export const AUDIENCE_GROUP_FIELD_KEYS = [
    'adset.lookalikeSlots',
    'adset.excludedAudienceSlots',
    'adset.interests',
];
/** ① … ⑳ — номер группы шаблона (с 0); дальше — «(21)». */
export const groupOrdinal = (index) => index >= 0 && index < 20 ? String.fromCodePoint(0x2460 + index) : `(${index + 1})`;
/** «① LAL23». */
export const groupTitle = (index, label) => `${groupOrdinal(index)} ${label}`;
/** Для читалки: «Группа шаблона 1, LAL23». */
export const groupAriaLabel = (index, label) => `Группа шаблона ${index + 1}, ${label}`;
/** Значение задано (пустой список и `null` — не задано, §3.6). */
export function hasValue(v) {
    if (v === undefined || v === null || v === '')
        return false;
    if (Array.isArray(v))
        return v.length > 0;
    return true;
}
/** Группа со своей аудиторией (Lookalike / исключения / интересы) — по её ШАБЛОННЫМ значениям. */
export function isAudienceGroup(values) {
    return AUDIENCE_GROUP_FIELD_KEYS.some((k) => hasValue(values[k]));
}
/**
 * Действующий Advantage+ аудитории группы (§3.6, правило сервера, UI ничего
 * не дописывает): явное значение группы → оно; аудиторная без явного → выкл;
 * иначе — общий выбор.
 */
export function groupAdvantage(values, common) {
    const own = values[ADVANTAGE_AUDIENCE_KEY];
    if (typeof own === 'boolean')
        return { on: own, source: 'own' };
    if (isAudienceGroup(values))
        return { on: false, source: 'audience-default' };
    return { on: common === true, source: 'common' };
}
/**
 * Состояние флага групп по `/health`: включены, выключены, или проверить не
 * удалось (сбой `/health`) — тогда экран не прячет группы молча, а говорит
 * «Не удалось проверить» с [Повторить] (§11.1, UA В6). Без `builderV4` групп нет.
 */
export function groupsFeature(health, failed, pending = false) {
    // сбой ПОВТОРНОГО запроса при уже полученном ответе — верим полученному (ревью adv P2-6)
    if (failed && health === undefined)
        return 'unknown';
    if (pending && health === undefined)
        return 'pending';
    const f = health?.features;
    return f?.builderV4 === true && f.adSetGroups === true ? 'on' : 'off';
}
/** Пределы групп — из реестра (`adSetGroupRules`), без реестра — константы контракта. */
export function groupRules(registry) {
    const r = registry.adSetGroupRules;
    return {
        templateMin: r?.templateMin ?? ADS_GROUP_LIMITS.templateMin,
        max: r?.max ?? ADS_GROUP_LIMITS.max,
        labelMax: r?.labelMax ?? ADS_GROUP_LIMITS.labelMax,
        maxAdSetsPerCampaign: r?.maxAdSetsPerCampaign ?? ADS_GROUP_LIMITS.maxAdSetsPerCampaign,
    };
}
export const GROUP_LABEL_HINT = 'Латиницей — попадёт в название группы объявлений в Meta. Тире — только «-», без диакритики. Например: LAL23, Yoga, US-only';
export const GROUP_LABEL_ERROR = 'Только латиница, цифры, пробел и _ . , % + $ ( ) / -';
export const GROUP_LABEL_EMPTY = 'Введите название латиницей';
/** Ошибка названия группы (форма); null — годится. `others` — названия остальных групп. */
export function groupLabelError(label, others) {
    if (label.trim() === '')
        return GROUP_LABEL_EMPTY;
    if (!isAdsGroupLabelValid(label))
        return GROUP_LABEL_ERROR;
    const dup = others.find((o) => o.label.toLowerCase() === label.toLowerCase());
    return dup ? `Такое название уже у группы ${groupOrdinal(dup.index)}` : null;
}
/**
 * Привести текст к допустимому названию группы — БЕЗ транслита (общего
 * транслита в support-bot нет, §15.3 п. 3; кириллицу переводит сервер, см.
 * `latinViaServer` в шаблоне): «–»/«—» → «-», лишние знаки отброшены, пробелы
 * схлопнуты, края обрезаны, ≤ 40. Ничего не осталось — пустая строка (поле
 * подсвечено «Введите название латиницей»).
 */
export function latinGroupLabel(raw, max = ADS_GROUP_LIMITS.labelMax) {
    const out = [...raw.normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[–—]/g, '-')]
        .filter((ch) => ADS_GROUP_LABEL_RE.test(ch))
        .join('')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, max)
        .trim();
    return /[A-Za-z0-9]/.test(out) ? out : '';
}
/** Название, которого ещё нет среди `taken` (без учёта регистра): «LAL», «LAL 2», … */
export function uniqueGroupLabel(label, taken) {
    if (label === '')
        return '';
    const lower = new Set(taken.map((t) => t.toLowerCase()));
    if (!lower.has(label.toLowerCase()))
        return label;
    for (let n = 2; n < 100; n++) {
        const tail = ` ${n}`;
        const cand = `${label.slice(0, ADS_GROUP_LIMITS.labelMax - tail.length).trim()}${tail}`;
        if (!lower.has(cand.toLowerCase()))
            return cand;
    }
    return '';
}
/** Префикс русского текста замечания группы (§3.5): «Группа шаблона «LABEL»: …». */
const GROUP_ISSUE_PREFIX_RE = /^Группа шаблона «([^»]*)»:\s*/;
/**
 * Текст замечания группы: префикс сервера «Группа шаблона «X»: » → «① X: »
 * (UA М4); номер — по ключу группы.
 */
export function groupIssueText(issue, groups) {
    const key = issue.groupKey ?? issue.details?.groupKey ?? null;
    const idx = key ? groups.findIndex((g) => g.key === key) : -1;
    const m = GROUP_ISSUE_PREFIX_RE.exec(issue.message);
    if (!m)
        return issue.message;
    const at = idx >= 0 ? idx : groups.findIndex((g) => g.label === m[1]);
    if (at < 0)
        return issue.message;
    return `${groupTitle(at, groups[at].label)}: ${issue.message.slice(m[0].length)}`;
}
/**
 * К какой группе шаблона (номер с 0) относится замечание: по `groupKey`
 * (замечание или его `details`), а у новой группы без ключа — по названию из
 * префикса «Группа шаблона «X»:» (ревью SB-7 P1-1). Не группы — null.
 */
export function issueGroupIndex(issue, groups) {
    const key = issue.groupKey ?? issue.details?.groupKey ?? null;
    if (key != null) {
        const at = groups.findIndex((g) => g.key === key);
        return at >= 0 ? at : null;
    }
    const m = GROUP_ISSUE_PREFIX_RE.exec(issue.message);
    if (!m)
        return null;
    const at = groups.findIndex((g) => g.label === m[1]);
    return at >= 0 ? at : null;
}
/** Замечание про группу шаблона (ключ или префикс), а не про общее поле. */
export function isGroupIssue(issue) {
    return ((issue.groupKey ?? issue.details?.groupKey ?? null) != null ||
        GROUP_ISSUE_PREFIX_RE.test(issue.message));
}
/**
 * Номера групп шаблона запуска (ревью SB-7 P2-1): по порядку шаблона, если
 * известен шаблон той же версии (или версия не задана), — снятая при запуске
 * ② не сдвигает ③; иначе — по первому появлению в запуске (лучшее, что есть
 * без шаблона). Ключ → номер с 0.
 */
export function groupOrder(refs, template, version) {
    const out = new Map();
    const tplGroups = template && (version == null || template.version === version)
        ? (template.adSetGroups?.groups ?? [])
        : [];
    tplGroups.forEach((g, i) => out.set(g.key, i));
    if (refs.every((r) => out.has(r.groupKey)))
        return out;
    out.clear();
    for (const r of refs)
        if (!out.has(r.groupKey))
            out.set(r.groupKey, out.size);
    return out;
}
/**
 * Номера групп, замороженные пачкой (`adSetGroups[].index` auto-ads, ревью E-2
 * P2): у всех ссылок есть номер — ключ → номер; иначе null (старый auto-ads —
 * тогда {@link groupOrder} по шаблону).
 */
export function frozenGroupOrder(refs) {
    if (refs.length === 0 || refs.some((r) => r.index == null))
        return null;
    return new Map(refs.map((r) => [r.groupKey, r.index]));
}
/** Список групп по ключам — «① LAL23, ② LAL45» (неизвестный ключ пропускается). */
export function groupListText(keys, groups) {
    return keys
        .map((k) => groups.findIndex((g) => g.key === k))
        .filter((i) => i >= 0)
        .map((i) => groupTitle(i, groups[i].label))
        .join(', ');
}
/** «2 группы шаблона × 3 копии = 6 групп объявлений» (§3.1) — только счёт, без денег. */
export function groupsCountText(groups, copies) {
    const g = `${groups} ${plural(groups, ['группа', 'группы', 'групп'])} шаблона`;
    const total = groups * copies;
    const t = `${total} ${plural(total, ['группа', 'группы', 'групп'])} объявлений`;
    if (copies <= 1)
        return `${g} = ${t}`;
    return `${g} × ${copies} ${plural(copies, ['копия', 'копии', 'копий'])} = ${t}`;
}
export function plural(n, [one, few, many]) {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m10 === 1 && m100 !== 11)
        return one;
    if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14))
        return few;
    return many;
}
//# sourceMappingURL=groups.js.map